/**
 * RedactionArgumentsCollapsible — liste pliable des arguments juridiques
 * identifiés pendant la phase de planification.
 *
 * Port web de `apps/addin-word/src/taskpane/components/hector-ai/ArgumentsCollapsible.tsx`.
 * Différences vs add-in : palette stone-* (vs gray-*), bg-stone-900 (vs bg-charcoal).
 *
 * ⚠️ Le `CollapsibleTrigger` de l'add-in est un wrapper custom qui pose
 * `w-full flex items-center` + chevron rotatif par défaut ; celui de
 * legalworkspace est le primitif Radix NU. Les classes de layout doivent donc
 * être portées ici au call-site (bug « bloc qui ne va pas jusqu'au bout »,
 * remonté user 2026-07-13) — chevron aligné sur le pattern des cartes JP
 * (`RedactionQueriesCollapsible`).
 */

import * as React from "react";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { ChevronRight, FileText, Scale, Loader2, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { LegalVerificationState } from "@/features/redaction/types/research-progress";

export interface RedactionArgumentsCollapsibleProps {
  titles: string[];
  isOpen?: boolean;
  legalVerification?: LegalVerificationState;
}

export function RedactionArgumentsCollapsible({
  titles,
  isOpen: controlledIsOpen = false,
  legalVerification,
}: RedactionArgumentsCollapsibleProps) {
  const [isOpen, setIsOpen] = React.useState(controlledIsOpen);

  React.useEffect(() => {
    setIsOpen(controlledIsOpen);
  }, [controlledIsOpen]);

  const formatVerificationTime = (ms: number): string => {
    return (ms / 1000).toFixed(1);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="w-full flex items-center gap-2 text-left border border-stone-100 hover:bg-stone-50 rounded-md p-2.5 transition-colors cursor-pointer">
        <ChevronRight
          className={cn(
            "w-3 h-3 text-stone-400 transition-transform shrink-0",
            isOpen && "rotate-90",
          )}
        />
        <FileText className="w-3.5 h-3.5 text-stone-400 shrink-0" />
        <span className="text-xs font-medium text-stone-700">Arguments juridiques identifiés</span>
        <span className="text-xs text-stone-400 ml-auto tabular-nums">{titles.length}</span>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="space-y-1.5 p-2.5 pt-1.5">
          {titles.map((title, index) => (
            <div key={index} className="flex items-start gap-2 text-xs text-stone-600">
              <span className="text-stone-400 tabular-nums shrink-0">{index + 1}.</span>
              <span className="leading-snug">{title}</span>
            </div>
          ))}
        </div>

        {/* Vérification des fondements juridiques */}
        {legalVerification &&
          (legalVerification.status === "in_progress" ||
            legalVerification.status === "complete") && (
            <div className="mx-2.5 mb-2.5 mt-1 p-2 bg-stone-50 border border-stone-100 rounded-md">
              <div className="flex items-center gap-2">
                <Scale className="w-3.5 h-3.5 text-stone-400 shrink-0" />
                <span className="text-xs text-stone-600">Vérification des fondements</span>
                <div className="ml-auto flex items-center gap-1.5">
                  {legalVerification.status === "in_progress" ? (
                    <>
                      <Loader2 className="w-3 h-3 text-stone-400 animate-spin" />
                      <span className="text-xs text-stone-400">en cours...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-3 h-3 text-stone-400" />
                      <span className="text-xs text-stone-500">
                        {legalVerification.totalArticlesVerified} art. en{" "}
                        {formatVerificationTime(legalVerification.verificationTimeMs ?? 0)}s
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
      </CollapsibleContent>
    </Collapsible>
  );
}
